import { getVisitId } from '../visitId';
import { AnalyticsEventName } from '../events';
import { MetricsReading, PointerKind } from './metrics';

/**
 * Version of the signal set this page collects.
 *
 * Raise it whenever what is measured changes — a renamed key, a new group, a different definition of
 * an existing number. Without the bump, rows from before and after the deploy stay comparable in
 * appearance and stop being comparable in fact, which is the failure that leaves no trace.
 */
export const COLLECTOR_VERSION = 1;

export const MAX_PATH_ENTRIES = 200;
export const MAX_SNAPSHOT_BYTES = 8192;

export type PathStepName = AnalyticsEventName | 'truncated';

export interface PathStep {
  e: PathStepName;
  t: number;
}

export interface VisitSignalsPayload {
  visitId: string;
  seq: number;
  collectorVersion: number;
  primaryPointer: PointerKind;
  interactionCount: number;
  firstInteractionMs: number | null;
  elapsedMs: number;
  signals: Record<string, unknown>;
  path: PathStep[];
}

let path: PathStep[] = [];

export const recordStep = (name: AnalyticsEventName, at: number): void => {
  path.push({ e: name, t: at });
};

/**
 * The first steps say how the visit began and the last say how it ended; the repetitions in between
 * are what a long visit fills the log with. So the middle is what a cap has to take.
 */
export const readPath = (): PathStep[] => {
  if (path.length <= MAX_PATH_ENTRIES) return [...path];
  const head = Math.floor((MAX_PATH_ENTRIES - 1) / 2);
  const tail = MAX_PATH_ENTRIES - 1 - head;
  return [
    ...path.slice(0, head),
    { e: 'truncated', t: path[head].t },
    ...path.slice(path.length - tail),
  ];
};

export const _resetPathForTests = (): void => {
  path = [];
};

export const buildSnapshot = (
  reading: MetricsReading,
  seq: number,
): VisitSignalsPayload => {
  const payload: VisitSignalsPayload = {
    visitId: getVisitId(),
    seq,
    collectorVersion: COLLECTOR_VERSION,
    primaryPointer: reading.primaryPointer,
    interactionCount: reading.interactionCount,
    firstInteractionMs: reading.firstInteractionMs,
    elapsedMs: reading.elapsedMs,
    signals: {
      pointer: reading.pointer,
      scroll: reading.scroll,
      focus: reading.focus,
      timing: reading.timing,
      viewport: reading.viewport,
    },
    path: readPath(),
  };

  if (JSON.stringify(payload).length <= MAX_SNAPSHOT_BYTES) return payload;

  // Over the cap the path goes first: the distributions cannot be reconstructed from anything else,
  // while the shape of a path this long is already dominated by its repetitions.
  payload.path = payload.path.slice(0, 20);
  if (JSON.stringify(payload).length <= MAX_SNAPSHOT_BYTES) return payload;

  payload.path = [];
  return payload;
};
